import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, BookOpen, User, Send, ChevronDown,
  CheckCircle, AlertCircle, MessageSquare, ExternalLink
} from "lucide-react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtTs = (secs) => {
  if (!secs && secs !== 0) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const STATUS_COLORS = {
  pending:   { bg: "bg-[#FFCC00]/10", text: "text-[#FFCC00]" },
  in_review: { bg: "bg-[#00F0FF]/10", text: "text-[#00F0FF]" },
  answered:  { bg: "bg-[#00FF88]/10", text: "text-[#00FF88]" },
  closed:    { bg: "bg-gray-100 dark:bg-white/5",      text: "text-[#52525B]" },
};

const STATUS_LABELS = {
  pending:   "Pending",
  in_review: "In Review",
  answered:  "Answered",
  closed:    "Closed",
};

const ROLE_COLORS = {
  student: "bg-[#A855F7]/20 text-[#A855F7]",
  mentor:  "bg-[#00F0FF]/20 text-[#00F0FF]",
  admin:   "bg-[#FFCC00]/20 text-[#FFCC00]",
};

// ── DoubtThread ───────────────────────────────────────────────────────────────
export default function DoubtThread({ doubtId, onBack, onJumpToTimestamp }) {
  const { user } = useAuth();
  const [doubt, setDoubt] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const bottomRef = useRef(null);
  const textRef = useRef(null);

  const isStaff = user?.role === "admin" || user?.role === "mentor";

  const fetchAll = useCallback(async () => {
    try {
      const [dRes, mRes] = await Promise.all([
        axios.get(`${API}/doubts/${doubtId}`, { withCredentials: true }),
        axios.get(`${API}/doubts/${doubtId}/messages`, { withCredentials: true }),
      ]);
      setDoubt(dRes.data);
      setMessages(Array.isArray(mRes.data) ? mRes.data : []);
    } catch (e) {
      toast.error("Failed to load doubt");
    } finally {
      setLoading(false);
    }
  }, [doubtId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/doubts/${doubtId}/messages`, { message: reply }, { withCredentials: true });
      setReply("");
      fetchAll();
    } catch (e) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    if (!isStaff) return;
    setChangingStatus(true);
    try {
      await axios.put(`${API}/doubts/${doubtId}/status`, { status }, { withCredentials: true });
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
      fetchAll();
    } catch (e) {
      toast.error("Failed to update status");
    } finally {
      setChangingStatus(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00d991] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!doubt) {
    return (
      <div className="text-center py-16 text-gray-400 dark:text-[#52525B]">
        <AlertCircle className="w-10 h-10 mx-auto mb-3" />
        <p>Doubt not found</p>
      </div>
    );
  }

  const sc = STATUS_COLORS[doubt.status] || STATUS_COLORS.pending;

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 flex-shrink-0">
        {onBack && (
          <button onClick={onBack} className="text-gray-500 dark:text-[#A1A1AA] hover:text-gray-900 dark:text-white transition-colors p-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-gray-900 dark:text-white font-outfit font-bold text-base truncate">{doubt.lesson_name || "Doubt"}</h2>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge className={`border-0 text-[10px] font-jetbrains ${sc.bg} ${sc.text}`}>
              {STATUS_LABELS[doubt.status] || doubt.status}
            </Badge>
            {doubt.timestamp_in_video > 0 && (
              <span className="flex items-center gap-1 text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B]">
                <Clock className="w-3 h-3" /> {fmtTs(doubt.timestamp_in_video)}
              </span>
            )}
          </div>
        </div>
        {/* Status changer for staff */}
        {isStaff && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {["pending", "in_review", "answered", "closed"].map((s) => (
              <button
                key={s}
                disabled={changingStatus || doubt.status === s}
                onClick={() => handleStatusChange(s)}
                className={`text-[9px] font-jetbrains px-2 py-1 rounded transition-all ${
                  doubt.status === s
                    ? `${STATUS_COLORS[s]?.bg} ${STATUS_COLORS[s]?.text} font-bold`
                    : "bg-gray-100 dark:bg-white/5 text-[#52525B] hover:bg-white/10 hover:text-white"
                }`}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Doubt Meta Card */}
      <div className="bg-white dark:bg-[#0A0F1C] border border-gray-200 dark:border-white/[0.06] rounded-xl p-4 mb-4 flex-shrink-0">
        <div className="flex flex-wrap gap-4 text-xs mb-3">
          <span className="flex items-center gap-1.5 text-gray-500 dark:text-[#A1A1AA]">
            <User className="w-3.5 h-3.5 text-[#A855F7]" />
            {doubt.student_name}
          </span>
          {doubt.course_name && (
            <span className="flex items-center gap-1.5 text-gray-500 dark:text-[#A1A1AA]">
              <BookOpen className="w-3.5 h-3.5 text-[#00F0FF]" />
              {doubt.course_name}
            </span>
          )}
          {doubt.timestamp_in_video > 0 && (
            <span className="flex items-center gap-1.5 text-gray-500 dark:text-[#A1A1AA]">
              <Clock className="w-3.5 h-3.5 text-[#FFCC00]" />
              At {fmtTs(doubt.timestamp_in_video)} in video
              {onJumpToTimestamp && (
                <button
                  onClick={() => onJumpToTimestamp(doubt.timestamp_in_video)}
                  className="flex items-center gap-1 text-[#00d991] hover:text-gray-900 dark:text-white transition-colors ml-1"
                >
                  <ExternalLink className="w-3 h-3" /> Jump
                </button>
              )}
            </span>
          )}
          <span className="text-gray-400 dark:text-[#52525B]">{fmtDate(doubt.created_at)}</span>
        </div>

        {/* Original Question */}
        <div className="bg-[#A855F7]/5 border border-[#A855F7]/20 rounded-lg p-3">
          <p className="text-[10px] font-jetbrains text-[#A855F7] uppercase mb-1">Original Question</p>
          <p className="text-sm text-gray-900 dark:text-white leading-relaxed">{doubt.doubt_text}</p>
        </div>
      </div>

      {/* Messages thread */}
      <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y space-y-3 mb-4 pr-1" style={{ minHeight: 0 }}>
        {messages.length === 0 ? (
          <div className="py-8 text-center">
            <MessageSquare className="w-8 h-8 text-gray-400 dark:text-[#52525B] mx-auto mb-2" />
            <p className="text-sm text-gray-400 dark:text-[#52525B]">No replies yet. Be the first to respond!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender_id === user?.user_id;
            const roleColor = ROLE_COLORS[msg.sender_role] || ROLE_COLORS.student;
            return (
              <div key={msg.message_id} className={`flex gap-3 ${isMe ? "flex-row-reverse" : ""}`}>
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold font-outfit border ${
                  msg.sender_role === "admin" ? "bg-[#FFCC00]/10 border-[#FFCC00]/20 text-[#FFCC00]"
                  : msg.sender_role === "mentor" ? "bg-[#00F0FF]/10 border-[#00F0FF]/20 text-[#00F0FF]"
                  : "bg-[#A855F7]/10 border-[#A855F7]/20 text-[#A855F7]"
                }`}>
                  {msg.sender_name?.charAt(0)?.toUpperCase() || "?"}
                </div>

                <div className={`flex-1 max-w-[78%] ${isMe ? "items-end" : "items-start"} flex flex-col gap-1`}>
                  {/* Name + Role */}
                  <div className={`flex items-center gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
                    <span className="text-xs text-gray-900 dark:text-white font-medium font-outfit">{msg.sender_name}</span>
                    <span className={`text-[9px] font-jetbrains uppercase px-1.5 py-0.5 rounded ${roleColor}`}>
                      {msg.sender_role}
                    </span>
                  </div>

                  {/* Bubble */}
                  <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    isMe
                      ? "bg-[#00d991]/10 border border-[#00d991]/20 text-white rounded-tr-sm"
                      : "bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] text-[#CBD5E1] rounded-tl-sm"
                  }`}>
                    {msg.message}
                  </div>

                  <span className="text-[10px] text-gray-400 dark:text-[#52525B]">{fmtDate(msg.created_at)}</span>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Reply box */}
      {(isStaff || doubt.student_id === user?.user_id) && (
        <form onSubmit={handleSend} className="flex-shrink-0 flex gap-2">
          <textarea
            ref={textRef}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your reply... (Enter to send, Shift+Enter for newline)"
            rows={2}
            className="flex-1 bg-white dark:bg-[#0A0F1C] border border-gray-200 dark:border-white/[0.08] rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-[#52525B] resize-none focus:outline-none focus:border-[#00d991]/40 transition-colors"
          />
          <Button
            type="submit"
            disabled={!reply.trim() || sending}
            className="bg-[#00d991] text-black font-bold hover:bg-white self-end h-11 px-4 rounded-xl"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      )}
    </div>
  );
}
